import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Input,
    Row,
    Select,
    Skeleton,
    Space,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    BulbOutlined,
    GlobalOutlined,
    PlusOutlined,
    SaveOutlined,
    ShopOutlined,
    UndoOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { TEMPLATE_TYPES } from './templateTypes';
import {
    aiAdminApi,
    ModelBeliefsBrandConfigPatch,
    ModelBeliefsBrandConfigResponse,
    ModelBeliefsCategorySuggestions,
    ModelBeliefsCompetitorSuggestions,
    ModelBeliefsConfigSuggestions,
    ModelBeliefsPricePositioning,
} from '../../../services/ai-admin-api';

const { Text } = Typography;

// "2/3": named by two of the three suggestion runs. Nothing on a single run.
const votesTag = (votes: number | undefined, runs: number | undefined) =>
    runs && runs > 1 && votes ? (
        <Tooltip title={`Named by ${votes} of ${runs} suggestion runs`}>
            <Tag>{votes}/{runs}</Tag>
        </Tooltip>
    ) : null;
const votesText = (votes: number | undefined, runs: number | undefined) =>
    runs && runs > 1 && votes ? (
        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
            {votes}/{runs}
        </Text>
    ) : null;
const { TextArea } = Input;

// The editable shape, all of it, held as form state. Every field is sent on
// every save: the API decides by presence, so sending the whole set is what
// makes clearing a field possible at all.
interface FormState {
    consumerName: string;
    categoryLabel: string;
    marketCountry: string;
    marketCity: string;
    heroProducts: string[];
    competitorNames: string[];
    brandDomains: string[];
    impostorDomains: string[];
    disambiguationNote: string;
    pricePositioning: ModelBeliefsPricePositioning | '';
    // Ad-hoc targets only.
    name: string;
    websiteUrl: string;
    templateType: string;
}

const EMPTY: FormState = {
    consumerName: '',
    categoryLabel: '',
    marketCountry: 'AU',
    marketCity: '',
    heroProducts: [],
    competitorNames: [],
    brandDomains: [],
    impostorDomains: [],
    disambiguationNote: '',
    pricePositioning: '',
    name: '',
    websiteUrl: '',
    templateType: '',
};

const toForm = (
    response: ModelBeliefsBrandConfigResponse,
    fallbackName: string,
): FormState => ({
    consumerName: response.config?.consumerName ?? fallbackName,
    categoryLabel: response.config?.categoryLabel ?? '',
    marketCountry:
        response.config?.marketCountry ??
        response.markets?.[0]?.country ??
        'AU',
    marketCity: response.config?.marketCity ?? '',
    heroProducts: response.config?.heroProductsJson ?? [],
    competitorNames: response.config?.competitorNamesJson ?? [],
    brandDomains: response.config?.brandDomainsJson ?? [],
    impostorDomains: response.config?.impostorDomainsJson ?? [],
    disambiguationNote: response.config?.disambiguationNote ?? '',
    pricePositioning: response.config?.pricePositioning ?? '',
    name: response.config?.name ?? response.brand?.name ?? fallbackName,
    websiteUrl: response.config?.websiteUrl ?? '',
    templateType: response.config?.templateType ?? response.brand?.templateType ?? '',
});

// null rather than '' for the text fields: the API treats an empty string as a
// cleared value, and being explicit here keeps that decision in one place.
const toPatch = (form: FormState, adhoc: boolean): ModelBeliefsBrandConfigPatch => ({
    consumerName: form.consumerName.trim() || null,
    categoryLabel: form.categoryLabel.trim() || null,
    marketCountry: form.marketCountry.trim().toUpperCase() || null,
    marketCity: form.marketCity.trim() || null,
    heroProductsJson: form.heroProducts,
    competitorNamesJson: form.competitorNames,
    brandDomainsJson: form.brandDomains,
    impostorDomainsJson: form.impostorDomains,
    disambiguationNote: form.disambiguationNote.trim() || null,
    pricePositioning: form.pricePositioning || null,
    ...(adhoc
        ? {
              name: form.name.trim() || null,
              websiteUrl: form.websiteUrl.trim() || null,
              templateType: form.templateType.trim() || null,
          }
        : {}),
});

const tagOptions = (values: string[]) =>
    values.map((value) => ({ label: value, value }));

interface Props {
    // One of the two: a platform brand's config, or any config by id (an
    // ad-hoc target has no brand). The same editor either way.
    brandId?: number;
    configId?: number;
    brandName: string;
    // Lets the list refresh its row without re-fetching the whole table on
    // every keystroke-free save.
    onSaved?: () => void;
}

export const ModelBeliefsBrandConfigForm: React.FC<Props> = ({
    brandId,
    configId,
    brandName,
    onSaved,
}) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    // Every call goes by config id when that is what we were given, else by
    // brand; the API answers in one shape.
    const api = {
        get: () =>
            configId
                ? aiAdminApi.getModelBeliefsConfig(jwt, configId)
                : aiAdminApi.getModelBeliefsBrandConfig(jwt, brandId as number),
        put: (patch: ModelBeliefsBrandConfigPatch) =>
            configId
                ? aiAdminApi.putModelBeliefsConfig(jwt, configId, patch)
                : aiAdminApi.putModelBeliefsBrandConfig(jwt, brandId as number, patch),
        suggestCategories: () =>
            configId
                ? aiAdminApi.suggestModelBeliefsConfigCategories(jwt, configId)
                : aiAdminApi.suggestModelBeliefsCategories(jwt, brandId as number),
        suggestCompetitors: () =>
            configId
                ? aiAdminApi.suggestModelBeliefsConfigCompetitors(jwt, configId)
                : aiAdminApi.suggestModelBeliefsCompetitors(jwt, brandId as number),
    };

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] = useState<ModelBeliefsBrandConfigResponse | null>(
        null,
    );
    const [form, setForm] = useState<FormState>(EMPTY);
    const [saved, setSaved] = useState<FormState>(EMPTY);
    const [warnings, setWarnings] = useState<string[]>([]);
    // Most candidates are press and review coverage needing no decision, so the
    // list opens short rather than filling the drawer with rows nobody acts on.
    const [allDomains, setAllDomains] = useState(false);
    // v4 phase 0: categories suggested from the brand's own site.
    const [categories, setCategories] = useState<ModelBeliefsCategorySuggestions | null>(null);
    const [suggesting, setSuggesting] = useState(false);
    const [suggestions, setSuggestions] = useState<ModelBeliefsConfigSuggestions>({
        fromRunId: null,
        competitors: [],
        domains: [],
    });

    const load = useCallback(async () => {
        if (!jwt) return;
        setLoading(true);
        setError(null);
        try {
            const result = await api.get();
            const next = toForm(result, brandName);
            setResponse(result);
            setForm(next);
            setSaved(next);
            setCategories(result.config?.categorySuggestionsJson ?? null);
            setWarnings(result.warnings ?? []);
            setSuggestions(
                result.suggestions ?? {
                    fromRunId: null,
                    competitors: [],
                    domains: [],
                },
            );
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwt, brandId, configId, brandName]);

    useEffect(() => {
        void load();
    }, [load]);

    const dirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(saved),
        [form, saved],
    );
    const adhoc = !!configId && !response?.brandId;

    const suggestCategories = async () => {
        if (!jwt) return;
        setSuggesting(true);
        try {
            setCategories(await api.suggestCategories());
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setSuggesting(false);
        }
    };

    // Competitors from a model, for a brand no run has named any for. The
    // call reads the saved config, so the category label has to be saved
    // first; the button says so rather than failing on the API's message.
    const [modelCompetitors, setModelCompetitors] = useState<ModelBeliefsCompetitorSuggestions | null>(null);
    const [suggestingCompetitors, setSuggestingCompetitors] = useState(false);
    const suggestCompetitors = async () => {
        if (!jwt) return;
        setSuggestingCompetitors(true);
        try {
            setModelCompetitors(await api.suggestCompetitors());
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setSuggestingCompetitors(false);
        }
    };

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const addTo = (key: 'competitorNames' | 'brandDomains' | 'impostorDomains', value: string) =>
        setForm((prev) =>
            prev[key].some((v) => v.toLowerCase() === value.toLowerCase())
                ? prev
                : { ...prev, [key]: [...prev[key], value] },
        );

    const save = async () => {
        if (!jwt) return;
        setSaving(true);
        try {
            const result = await api.put(toPatch(form, adhoc));
            // The server normalises (domains reduced to registrable, blanks
            // dropped), so the form is reset from what came back rather than
            // from what was typed.
            const next = toForm(result, brandName);
            setResponse((prev) => ({ ...(prev ?? result), ...result }));
            setForm(next);
            setSaved(next);
            setWarnings(result.warnings ?? []);
            setSuggestions(
                result.suggestions ?? { fromRunId: null, competitors: [], domains: [] },
            );
            message.success('Saved.');
            onSaved?.();
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Skeleton active paragraph={{ rows: 10 }} />;

    // What a platform brand carries on its own record, an ad-hoc target
    // carries here.
    const adhocCard = adhoc && (
        <Card size="small" title="The brand (not on the platform)">
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: 13 }}>Brand name</Text>
                        <Input
                            value={form.name}
                            status={form.name.trim() ? undefined : 'error'}
                            onChange={(e) => set('name', e.target.value)}
                        />
                    </Space>
                </Col>
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: 13 }}>Website</Text>
                        <Input
                            value={form.websiteUrl}
                            placeholder="https://"
                            onChange={(e) => set('websiteUrl', e.target.value)}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Where the category, the competitors and the price positioning are
                            suggested from.
                        </Text>
                    </Space>
                </Col>
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: 13 }}>Question template</Text>
                        <Select
                            value={form.templateType || undefined}
                            placeholder="durable"
                            onChange={(value) => set('templateType', value)}
                            options={TEMPLATE_TYPES.map((t) => ({ value: t, label: t }))}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Which statements a read asks. A read of one template is not a read
                            of another.
                        </Text>
                    </Space>
                </Col>
            </Row>
        </Card>
    );
    if (error) return <Alert type="error" showIcon message={error} />;

    const markets = response?.markets ?? [];
    const limits = response?.limits;
    const unusedSuggestions =
        suggestions.competitors.filter(
            (c) =>
                !form.competitorNames.some(
                    (v) => v.toLowerCase() === c.name.toLowerCase(),
                ),
        );
    const unusedDomains = suggestions.domains.filter(
        (d) =>
            !form.brandDomains.includes(d.domain) &&
            !form.impostorDomains.includes(d.domain),
    );

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {warnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message="This config would affect what the read measures"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {warnings.map((w) => (
                                <li key={w}>{w}</li>
                            ))}
                        </ul>
                    }
                />
            )}

            {adhocCard}

            <Card size="small" title="What the models are asked about">
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>Name as surveyed</Text>
                            <Input
                                value={form.consumerName}
                                placeholder={brandName}
                                onChange={(e) => set('consumerName', e.target.value)}
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                What its customers call it, not the company that owns it.
                                Nobody bets with Tabcorp; they bet with TAB. Asking about
                                the parent measures a different brand.
                            </Text>
                        </Space>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space size={4}>
                                <Text strong style={{ fontSize: 13 }}>Category label</Text>
                                <Tag color={form.categoryLabel.trim() ? 'green' : 'red'}>
                                    {form.categoryLabel.trim() ? 'set' : 'required'}
                                </Tag>
                            </Space>
                            <Input
                                value={form.categoryLabel}
                                placeholder="linen bedding"
                                status={form.categoryLabel.trim() ? undefined : 'error'}
                                onChange={(e) => set('categoryLabel', e.target.value)}
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                A shopper&rsquo;s words for what they are buying, and never
                                derived from the brand name. This text goes into every gate
                                question, so &ldquo;{brandName} style bedding&rdquo; would
                                leak the brand and report near-total inclusion. Without a
                                label the awareness gate is skipped entirely.
                            </Text>
                            <Space size={8} wrap style={{ marginTop: 6 }}>
                                <Tooltip title="Reads the brand's homepage (its saved brand domain, else the organisation's website) and asks the suggestion model (Hub: Configure), with web search, for up to five categories a shopper searches for separately. One call, a few cents. Nothing is saved to the label until you pick one and save.">
                                    <Button
                                        size="small"
                                        icon={<BulbOutlined style={{ marginRight: 4 }} />}
                                        loading={suggesting}
                                        onClick={() => void suggestCategories()}
                                    >
                                        {categories ? 'Suggest again' : 'Suggest from website'}
                                    </Button>
                                </Tooltip>
                                {categories && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        From {categories.site}, {new Date(categories.suggestedAt).toLocaleDateString()}
                                    </Text>
                                )}
                            </Space>
                        </Space>
                    </Col>
                </Row>

                {categories && categories.suggestions.length > 0 && (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(150px, max-content) max-content 1fr max-content',
                            alignItems: 'center',
                            columnGap: 14,
                            rowGap: 8,
                            marginTop: 14,
                            padding: '10px 12px',
                            background: '#fafafa',
                            borderRadius: 6,
                        }}
                    >
                        {categories.suggestions.map((c) => {
                            const chosen = form.categoryLabel.trim().toLowerCase() === c.phrase;
                            return (
                                <React.Fragment key={c.phrase}>
                                    <Text strong style={{ fontSize: 13 }}>{c.phrase}</Text>
                                    <Space size={4}>
                                        {c.main && <Tag color="gold" style={{ marginRight: 0 }}>main</Tag>}
                                        {votesTag(c.votes, categories.runs)}
                                    </Space>
                                    <Text type="secondary" style={{ fontSize: 12, minWidth: 0 }}>
                                        {c.covers}
                                    </Text>
                                    <Button
                                        size="small"
                                        type={chosen ? 'default' : 'link'}
                                        disabled={chosen}
                                        onClick={() => set('categoryLabel', c.phrase)}
                                    >
                                        {chosen ? 'In use' : 'Use this'}
                                    </Button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}

                <Divider style={{ margin: '16px 0' }} />

                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={8}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>Market (country)</Text>
                            <Input
                                value={form.marketCountry}
                                maxLength={2}
                                placeholder="AU"
                                onChange={(e) =>
                                    set('marketCountry', e.target.value.toUpperCase())
                                }
                            />
                            {markets.length > 0 ? (
                                <Space size={4} wrap>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        Surveyed in:
                                    </Text>
                                    {markets.map((m) => (
                                        <Tag
                                            key={m.country}
                                            color={
                                                m.country === form.marketCountry
                                                    ? 'blue'
                                                    : 'default'
                                            }
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => set('marketCountry', m.country)}
                                        >
                                            {m.country} · {m.surveys}
                                        </Tag>
                                    ))}
                                </Space>
                            ) : (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    No survey has a country yet, so there is nothing to
                                    match the read against.
                                </Text>
                            )}
                        </Space>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>City (optional)</Text>
                            <Input
                                value={form.marketCity}
                                placeholder="Melbourne"
                                onChange={(e) => set('marketCity', e.target.value)}
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Where the shopper is standing on every call. A city sharpens
                                local results; leave it off for a national read.
                            </Text>
                        </Space>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>Disambiguation note</Text>
                            <TextArea
                                value={form.disambiguationNote}
                                rows={3}
                                placeholder="the Australian betting app, not the writing app"
                                onChange={(e) =>
                                    set('disambiguationNote', e.target.value)
                                }
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Only for a name that means two things. It tells the models
                                which one is being asked about.
                            </Text>
                        </Space>
                    </Col>
                </Row>

                <Divider style={{ margin: '16px 0' }} />

                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Text strong style={{ fontSize: 13 }}>Price positioning</Text>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: categories?.pricePositioning
                                ? '320px max-content 1fr max-content'
                                : '320px',
                            alignItems: 'center',
                            columnGap: 12,
                        }}
                    >
                        <Select
                            style={{ width: 320 }}
                            value={form.pricePositioning}
                            onChange={(value) => set('pricePositioning', value)}
                            options={[
                                { value: '', label: 'Not set' },
                                { value: 'premium', label: 'Premium: a high price is the brand\'s side' },
                                { value: 'value', label: 'Value: a low price is the brand\'s side' },
                            ]}
                        />
                        {categories?.pricePositioning && (
                            <>
                                <Space size={6}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>From the site:</Text>
                                    <Tag color={categories.pricePositioning.positioning === 'unclear' ? undefined : 'blue'} style={{ marginRight: 0 }}>
                                        {categories.pricePositioning.positioning}
                                    </Tag>
                                    {votesTag(categories.pricePositioning.votes, categories.pricePositioning.runs)}
                                </Space>
                                <Text type="secondary" style={{ fontSize: 12, minWidth: 0 }}>
                                    {categories.pricePositioning.evidence ? `\u201c${categories.pricePositioning.evidence}\u201d` : ''}
                                </Text>
                                {categories.pricePositioning.positioning !== 'unclear' ? (
                                    <Button
                                        size="small"
                                        type={form.pricePositioning === categories.pricePositioning.positioning ? 'default' : 'link'}
                                        disabled={form.pricePositioning === categories.pricePositioning.positioning}
                                        onClick={() =>
                                            set(
                                                'pricePositioning',
                                                categories.pricePositioning!.positioning as ModelBeliefsPricePositioning,
                                            )
                                        }
                                    >
                                        {form.pricePositioning === categories.pricePositioning.positioning ? 'In use' : 'Use this'}
                                    </Button>
                                ) : (
                                    <span />
                                )}
                            </>
                        )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Which side of the price the brand is on. It sets the direction of every
                        price word in the read. While it is not set, the read writes no record fix
                        on price. &ldquo;Suggest from website&rdquo; above reads it from the site
                        for you to confirm.
                    </Text>
                </Space>
            </Card>

            <Card
                size="small"
                title="Competitors"
                extra={
                    limits ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {limits.readCompetitors
                                ? `first ${limits.maxCompetitors} are read`
                                : 'competitor reads are off'}
                        </Text>
                    ) : null
                }
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        value={form.competitorNames}
                        options={tagOptions(form.competitorNames)}
                        onChange={(values: string[]) => set('competitorNames', values)}
                        tokenSeparators={[',', '\n']}
                        placeholder="Cultiver, Sheridan, Sheet Society"
                    />
                    {/* Order is meaningful, not cosmetic: the planner takes the
                        first N and drops the rest without a word. */}
                    {limits?.readCompetitors && form.competitorNames.length > 0 && (
                        <Space size={4} wrap>
                            {form.competitorNames.map((name, index) => (
                                <Tag
                                    key={name}
                                    color={
                                        index < limits.maxCompetitors ? 'blue' : 'default'
                                    }
                                >
                                    {index + 1}. {name}
                                    {index < limits.maxCompetitors ? '' : ' · not read'}
                                </Tag>
                            ))}
                        </Space>
                    )}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Read alongside the brand, and matched in the gate&rsquo;s listings so
                        a competitor named by a model is recognised rather than counted as a
                        stranger. Competitor reads are about a third of a run&rsquo;s cost,
                        which is why only the first few are read.
                    </Text>

                    {unusedSuggestions.length > 0 && (
                        <Alert
                            type="info"
                            showIcon
                            message={
                                suggestions.fromRunId
                                    ? `Named by the models in run #${suggestions.fromRunId} and not configured`
                                    : 'Named by the models and not configured'
                            }
                            description={
                                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                    <Text style={{ fontSize: 12 }}>
                                        The gate asks a category question and reports whoever
                                        the models list, so this is competitor intelligence
                                        nobody had to ask for. The count is how many answers
                                        named them.
                                    </Text>
                                    <Space size={4} wrap>
                                        {unusedSuggestions.map((c) => (
                                            <Button
                                                key={c.name}
                                                size="small"
                                                icon={<PlusOutlined style={{ marginRight: 4 }} />}
                                                onClick={() => addTo('competitorNames', c.name)}
                                            >
                                                {c.name} ({c.count})
                                            </Button>
                                        ))}
                                    </Space>
                                </Space>
                            }
                        />
                    )}

                    <Space size={8} wrap>
                        <Tooltip
                            title={
                                saved.categoryLabel.trim()
                                    ? 'Reads the brand\u2019s homepage and asks the suggestion model (Hub: Configure), with web search, which brands a shopper would weigh it against when buying the category. One call, a few cents. Nothing is added until you pick.'
                                    : 'Save a category label first: competitors are the brands that sell the same kind of product.'
                            }
                        >
                            <Button
                                size="small"
                                icon={<BulbOutlined style={{ marginRight: 4 }} />}
                                loading={suggestingCompetitors}
                                disabled={!saved.categoryLabel.trim()}
                                onClick={() => void suggestCompetitors()}
                            >
                                {modelCompetitors ? 'Suggest again' : 'Suggest competitors'}
                            </Button>
                        </Tooltip>
                        {modelCompetitors && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                For {modelCompetitors.category}, from {modelCompetitors.site}
                            </Text>
                        )}
                    </Space>
                    {modelCompetitors && (
                        <Space size={4} wrap>
                            {modelCompetitors.suggestions
                                .filter(
                                    (c) =>
                                        !form.competitorNames.some(
                                            (name) => name.trim().toLowerCase() === c.name.toLowerCase(),
                                        ),
                                )
                                .map((c) => (
                                    <Tooltip key={c.name} title={c.why || undefined}>
                                        <Button
                                            size="small"
                                            icon={<PlusOutlined style={{ marginRight: 4 }} />}
                                            onClick={() => addTo('competitorNames', c.name)}
                                        >
                                            {c.name}
                                            {votesText(c.votes, modelCompetitors.runs)}
                                        </Button>
                                    </Tooltip>
                                ))}
                        </Space>
                    )}
                </Space>
            </Card>

            <Card size="small" title="Domains">
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space size={4}>
                                <ShopOutlined />
                                <Text strong style={{ fontSize: 13 }}>
                                    The brand&rsquo;s own domains
                                </Text>
                            </Space>
                            <Select
                                mode="tags"
                                style={{ width: '100%' }}
                                value={form.brandDomains}
                                options={tagOptions(form.brandDomains)}
                                onChange={(values: string[]) => set('brandDomains', values)}
                                tokenSeparators={[',', ' ', '\n']}
                                placeholder="bedthreads.com.au"
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Paste a URL if that is easier; it is stored reduced to the
                                registrable domain. Without these the brand&rsquo;s own site
                                is never counted as its own source, and the impostor check
                                flags it instead.
                            </Text>
                        </Space>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space size={4}>
                                <GlobalOutlined />
                                <Text strong style={{ fontSize: 13 }}>
                                    Known impostor domains
                                </Text>
                            </Space>
                            <Select
                                mode="tags"
                                style={{ width: '100%' }}
                                value={form.impostorDomains}
                                options={tagOptions(form.impostorDomains)}
                                onChange={(values: string[]) =>
                                    set('impostorDomains', values)
                                }
                                tokenSeparators={[',', ' ', '\n']}
                                placeholder="evasleep.shop"
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Sites passing themselves off as the brand. Listing one stops
                                it being re-flagged on every read.
                            </Text>
                        </Space>
                    </Col>
                </Row>

                {unusedDomains.length > 0 && (
                    <Alert
                        style={{ marginTop: 12 }}
                        type="info"
                        showIcon
                        message="Lookalike domains the models are reading"
                        description={
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <Text style={{ fontSize: 12 }}>
                                    Each carries the brand&rsquo;s name in its own domain
                                    without being a configured brand domain &mdash; which is
                                    what a site passing itself off looks like, and what press
                                    coverage does not. Until the brand&rsquo;s own site is
                                    listed above it appears here too, so say which each one
                                    is.
                                </Text>
                                {(allDomains
                                    ? unusedDomains
                                    : unusedDomains.slice(0, 6)
                                ).map((d) => (
                                    <Space key={d.domain} size={6} wrap>
                                        <Text code>{d.domain}</Text>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {d.count} citation{d.count === 1 ? '' : 's'}
                                        </Text>
                                        {d.evidence.length > 0 && (
                                            <Tooltip title={d.evidence.join(' | ')}>
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    evidence
                                                </Text>
                                            </Tooltip>
                                        )}
                                        <Button
                                            size="small"
                                            onClick={() => addTo('brandDomains', d.domain)}
                                        >
                                            It&rsquo;s the brand
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            onClick={() => addTo('impostorDomains', d.domain)}
                                        >
                                            It&rsquo;s an impostor
                                        </Button>
                                    </Space>
                                ))}
                                {unusedDomains.length > 6 && (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ paddingLeft: 0 }}
                                        onClick={() => setAllDomains((v) => !v)}
                                    >
                                        {allDomains
                                            ? 'Show fewer'
                                            : `Show ${unusedDomains.length - 6} more`}
                                    </Button>
                                )}
                            </Space>
                        }
                    />
                )}
            </Card>

            <Card size="small" title="Hero products">
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        value={form.heroProducts}
                        options={tagOptions(form.heroProducts)}
                        onChange={(values: string[]) => set('heroProducts', values)}
                        tokenSeparators={[',', '\n']}
                        placeholder="French flax linen sheet set"
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Stored, not yet asked: the product-level read is a later phase.
                        Nothing in a read changes if you fill this in today.
                    </Text>
                </Space>
            </Card>

            {response?.config && (
                <Descriptions size="small" column={3} bordered>
                    <Descriptions.Item label="Config row">
                        #{response.config.id}
                    </Descriptions.Item>
                    <Descriptions.Item label="Organisation">
                        {response.config.organisationId}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last saved">
                        {response.config.updatedAt
                            ? new Date(response.config.updatedAt).toLocaleString()
                            : '-'}
                    </Descriptions.Item>
                </Descriptions>
            )}

            <Space>
                <Button
                    type="primary"
                    icon={<SaveOutlined style={{ marginRight: 4 }} />}
                    loading={saving}
                    disabled={!dirty}
                    onClick={() => void save()}
                >
                    Save config
                </Button>
                <Button
                    icon={<UndoOutlined style={{ marginRight: 4 }} />}
                    disabled={!dirty || saving}
                    onClick={() => setForm(saved)}
                >
                    Revert
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    A run freezes this config when it is enqueued, so a save affects the
                    next read and never a read in flight.
                </Text>
            </Space>
        </Space>
    );
};
